import React, { useState } from 'react';
import { Form } from 'react-final-form';
import Axios from 'axios';
import { FORM_ERROR } from 'final-form';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { useMutation } from '@tanstack/react-query';
import * as Routes from 'modules/routes';
import { Button, SubmitButton } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import PasswordRequirements from '@/components/shared/PasswordRequirements';
import { strictPasswordValidationError } from '@/components/shared/PasswordRequirements/helpers';
import { SubmitError } from '@/components/common/Forms/Utils';
import PasswordField from '@/components/common/Forms/User/PasswordField';
import ContactAdminButton from '@/components/staffer/registrations/ContactAdminButton';
import Fields from './Fields';

const LABEL_WIDTH = 150;

interface EditStudentFormProps {
  classroomId: number | string;
  hasStrictPasswordRequirement: boolean;
  isAutoRosterCsv?: boolean;
  isNewStudent?: boolean;
  programId: number | string;
  stafferId: number | string;
  student?: {
    id?: number | string;
    first_name?: string;
    last_name?: string;
    username?: string;
  }
}

interface FormValues {
  first_name?: string;
  last_name?: string;
  password?: string;
  password_confirmation?: string;
  username?: string;
}

const EditStudentForm = ({
  classroomId,
  hasStrictPasswordRequirement,
  isAutoRosterCsv = false,
  isNewStudent = false,
  programId,
  stafferId,
  student,
}: EditStudentFormProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const classroomStudentsPath = Routes.teacher_classroom_students_path(classroomId, { programId });

  const { mutateAsync: saveStudent, isLoading } = useMutation({
    mutationFn: (values: FormValues) => {
      const headers = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      if (isNewStudent) {
        return Axios.post(Routes.teacher_classroom_students_path(classroomId), { student: values }, headers);
      }

      return Axios.put(
        Routes.teacher_update_classroom_student_path(classroomId, { id: student?.id }),
        { student: values },
        headers,
      );
    },
    onSuccess: () => {
      window.location.href = classroomStudentsPath;
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await saveStudent(values);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { errors?: string } } })?.response?.data?.errors
        || 'An error occurred. Please try again.';

      return { [FORM_ERROR]: errorMessage };
    }
  };

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    // Password is required for new students
    if (isNewStudent && !values.password) {
      errors.password = 'Password is required.';
    }

    if (hasStrictPasswordRequirement && values.password) {
      const message = strictPasswordValidationError(values.password);
      if (message) errors.password = message;
    }

    if (values.password !== values.password_confirmation) {
      errors.password_confirmation = 'Passwords do not match.';
    }

    return errors;
  };

  const autoRosterCsvDescription = () => (
    <div className="tw-py-3">
      <p>
        <Icon className="tw-text-red tw-mr-0.5" icon="Warning" size={14} />

        Your district manages all students' passwords. To request this student's password, please contact your district
        administrator.
        <br />
        If this student needs immediate access, click on the button below to create a temporary password.
      </p>

      <div className="tw-inline-block tw-mr-3">
        <ContactAdminButton
          getAdminInfoPath={Routes.contact_admin_teacher_setting_path(stafferId, { format: 'json' })}
        />
      </div>

      <Button onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
        Create temporary password
      </Button>
    </div>
  );

  const passwordFields = (values: FormValues) => {
    // For edit mode with auto roster CSV, allow toggling password visibility
    if (!isNewStudent && isAutoRosterCsv && !isPasswordVisible) return null;

    const title = isAutoRosterCsv ? 'Temporary Password' : (isNewStudent ? 'Password' : 'New Password');
    const confirmTitle = isAutoRosterCsv ? 'Confirm Temporary Password' : 'Confirm Password';
    const labelWidth = isNewStudent ? 0 : LABEL_WIDTH;

    return (
      <>
        {!isNewStudent && (
          <p className="tw-py-4">
            {`${isAutoRosterCsv ? 'Temporarily reset' : 'Reset'} the student's password
            by typing in the new password and typing the new password again.`}
            <br />
            {`You will need to tell your student their ${isAutoRosterCsv ? 'temporary' : 'new'} password.`}
          </p>
        )}

        <PasswordField
          displayError={false}
          htmlFor="password"
          isHorizontal={!isNewStudent}
          label={title}
          labelWidth={labelWidth}
        />
        {hasStrictPasswordRequirement && (
          <PasswordRequirements
            maxWidth={230}
            password={values.password || ''}
            spacerWidth={labelWidth}
          />
        )}
        <PasswordField
          htmlFor="password_confirmation"
          isHorizontal={!isNewStudent}
          label={confirmTitle}
          labelWidth={labelWidth}
        />
      </>
    );
  };

  return (
    <Form
      initialValues={{
        first_name: student?.first_name ?? '',
        last_name: student?.last_name ?? '',
        password: '',
        password_confirmation: '',
        username: student?.username ?? '',
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, pristine, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <Fields.TextField
            id="last-name"
            isHorizontal={!isNewStudent}
            label="Last Name"
            labelWidth={LABEL_WIDTH}
            name="last_name"
          />
          <Fields.TextField
            id="first-name"
            isHorizontal={!isNewStudent}
            label="First Name"
            labelWidth={LABEL_WIDTH}
            name="first_name"
          />
          <Fields.TextField
            id="username"
            isHorizontal={!isNewStudent}
            label={isNewStudent ? 'Desired Username' : 'Username'}
            labelWidth={LABEL_WIDTH}
            name="username"
          />

          {!isNewStudent && isAutoRosterCsv && autoRosterCsvDescription()}
          {passwordFields(values)}

          <SubmitError error={submitError} />

          <div className="mt20">
            <SubmitButton
              disabled={isNewStudent ? isLoading : pristine}
              submitting={isLoading}
            >
              {isNewStudent ? 'Add Student' : 'Update Student'}
            </SubmitButton>

            <a
              className="btn btn--link-purple"
              href={classroomStudentsPath}
            >
              Cancel
            </a>
          </div>
        </form>
      )}
    </Form>
  );
};

const EditStudentFormWrapper = (props: EditStudentFormProps) => (
  <ReactQueryProvider>
    <EditStudentForm {...props} />
  </ReactQueryProvider>
);

export default EditStudentFormWrapper;
